/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useState } from "react";
import { FaEye, FaRegEdit } from "react-icons/fa";
import Image from "assets/avatar.png";
import Modal from "components/Modal";
import MediaPreview, { useMediaPreview } from "components/MediaPreview";
import ReactCrop, {
  type Crop,
  type PixelCrop,
  centerCrop,
  makeAspectCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { PROFILE_HEADER_ACTIONS_SLOT_ID } from "./headerActionsSlot";
import { TruncatedText } from "pages/AdminConfig/components/TruncatedText";

// ── Helpers ───────────────────────────────────────────────────────────────────

// Generates a cropped File from the image element and the confirmed pixel crop area
async function getCroppedImg(
  image: HTMLImageElement,
  pixelCrop: PixelCrop
): Promise<File> {
  const canvas = document.createElement("canvas");
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas context not available");
  ctx.drawImage(
    image,
    pixelCrop.x * scaleX,
    pixelCrop.y * scaleY,
    pixelCrop.width * scaleX,
    pixelCrop.height * scaleY,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Canvas is empty"));
          return;
        }
        resolve(new File([blob], "profile.jpg", { type: "image/jpeg" }));
      },
      "image/jpeg",
      0.92
    );
  });
}

// ── Interface ─────────────────────────────────────────────────────────────────

interface ProfileHeaderProps {
  user: any;
  // Receives the cropped File ready to upload — replaces the raw event handler
  onImageFileReady: (file: File) => void;
  previewImage: string | null;
  imageUploading: boolean;
  onUpdateProfile: () => void;
  showEditProfile: boolean;
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Profile identity header — profile photo, name, designation and the Edit
 * Profile button. Rendered ABOVE the profile navigation tabs (mirroring the
 * Employee Details page, where the profile row sits above the tab bar) so it
 * stays visible across every tab. Owns the avatar view / crop / upload flow.
 */
function ProfileHeader({
  user,
  onImageFileReady,
  previewImage,
  imageUploading,
  onUpdateProfile,
  showEditProfile,
}: Readonly<ProfileHeaderProps>) {
  const [showImageActions, setShowImageActions] = useState(false);

  // Reusable full-screen media lightbox for viewing the profile photo.
  const { previewProps, open: openImagePreview } = useMediaPreview();

  // Crop modal state
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const imgRef = useRef<HTMLImageElement>(null);
  console.log("completedCrop", completedCrop, crop);
  console.log("User details", user);

  const profileImageSrc = imageUploading
    ? user?.imageUrl || Image
    : previewImage || user?.imageUrl || Image;

  // Case-insensitive status check — the API may return "ACTIVE" or "active".
  const isActive = String(user?.userStatus ?? "").toLowerCase() === "active";

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    if (img.src !== Image) img.src = Image;
  };

  // Intercepts the file input — reads as data URL and opens the crop modal
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so the same file can be re-selected after cancel
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCropSrc(reader.result as string);
      setCrop(undefined);
      setCompletedCrop(undefined);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  // Sets a centered square crop once the image renders inside ReactCrop
  const onCropImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const initialCrop = centerCrop(
      makeAspectCrop({ unit: "%", width: 90 }, 1, width, height),
      width,
      height
    );
    setCrop(initialCrop);
  };

  // Draws the confirmed crop to a canvas and passes the resulting File to the parent
  const handleCropConfirm = async () => {
    if (!imgRef.current || !completedCrop) return;
    try {
      const croppedFile = await getCroppedImg(imgRef.current, completedCrop);
      onImageFileReady(croppedFile);
    } catch (e) {
      console.error("Crop failed", e);
    }
    setShowCropModal(false);
    setCropSrc(null);
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setCropSrc(null);
  };

  const handleAvatarFocus = () => {
    setShowImageActions(true);
  };

  const handleAvatarBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const nextFocused = event.relatedTarget as Node | null;
    if (nextFocused && event.currentTarget.contains(nextFocused)) {
      return;
    }
    setShowImageActions(false);
  };

  return (
    <>
      {/* ── Profile header card — compact horizontal row (no empty banner space) ── */}
      <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700  ">
        <div className="flex items-center gap-4 px-1 pb-2">
          {/* Small rounded profile image with view / edit overlay + active indicator */}
          <div className="relative shrink-0">
            <div
              className="relative w-14 h-14 rounded-full border-2 border-white dark:border-gray-700 ring-1 ring-gray-200 dark:ring-gray-600 shadow-sm overflow-hidden cursor-pointer select-none"
              onMouseEnter={() => setShowImageActions(true)}
              onMouseLeave={() => setShowImageActions(false)}
              onFocus={handleAvatarFocus}
              onBlur={handleAvatarBlur}
              role="group"
              tabIndex={0}
              aria-label="Profile photo actions"
            >
              <img
                src={profileImageSrc}
                alt="Profile"
                className="w-full h-full object-cover"
                // className="w-12 h-12 rounded-full object-cover ring-2 ring-blue-100 dark:ring-gray-600 border-2 border-white dark:border-gray-700 flex-shrink-0"
                loading="lazy"
                onError={handleImageError}
              />

              {/* Eye + Edit overlay */}
              <div
                className={`absolute inset-0 bg-black/50 flex items-center justify-center gap-2 transition-opacity duration-200 ${showImageActions ? "opacity-100" : "opacity-0 pointer-events-none"
                  }`}
              >
                <button
                  type="button"
                  aria-label="View profile photo"
                  title="View profile photo"
                  onClick={() =>
                    openImagePreview(
                      [
                        {
                          url: profileImageSrc,
                          name: "Profile Photo",
                          // Profile photos are always images (jpg/png), but the URL
                          // may carry no extension — pass an explicit image mimeType
                          // so MediaPreview renders the image viewer, not the file
                          // fallback. "image/*" covers both jpg and png.
                          mimeType: "image/jpeg",
                        },
                      ],
                      0
                    )
                  }
                  className="text-white hover:text-blue-300 transition-colors"
                >
                  <FaEye size={14} />
                </button>
                <label
                  htmlFor="profileImageInput"
                  className="text-white hover:text-blue-300 transition-colors cursor-pointer"
                  title="Update profile photo"
                >
                  <FaRegEdit size={13} />
                </label>
              </div>

              {/* Upload spinner */}
              {imageUploading && (
                <div className="absolute inset-0 bg-black/40 z-30 flex items-center justify-center">
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      border: "3px solid rgba(255,255,255,0.3)",
                      borderTop: "3px solid #2563eb",
                      animation: "spin 0.8s linear infinite",
                    }}
                  />
                  <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
                </div>
              )}
            </div>

            {/* Status indicator — green dot when active, gray when inactive */}
            <span
              className={`absolute top-0 right-0 block w-4 h-4 rounded-full ring-2 ring-white dark:ring-gray-800 ${isActive ? "bg-green-500" : "bg-gray-400"
                }`}
              title={isActive ? "Active" : "Inactive"}
              aria-label={isActive ? "Active" : "Inactive"}
            />
          </div>

          {/* Hidden file input — onChange opens crop modal instead of uploading directly */}
          <input
            id="profileImageInput"
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/bmp,image/tiff"
            title="Click here for update profile Photo"
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Name + designation (gap-4 from the image) */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              <TruncatedText
                text={user.displayName || "Unknown User"}
                className="text-base font-semibold text-gray-900 dark:text-white capitalize w-[180px] sm:w-[250px] md:w-[150px]"
              />
            </div>

            <TruncatedText
              text={user.designation || ""}
              className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 w-[180px] sm:w-[250px] md:w-[400px]"
            />
          </div>

          <div className="flex items-center gap-3 shrink-0">
            {showEditProfile && (
              <button
                onClick={onUpdateProfile}
                type="button"
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors shrink-0"
              >
                <span style={{ letterSpacing: "0.01em", fontVariantLigatures: "none" }}>
                  Edit Profile
                </span>
              </button>
            )}
            <div id={PROFILE_HEADER_ACTIONS_SLOT_ID} className="flex items-center gap-3" />
          </div>
        </div>
      </div>

      {/* ── Profile photo lightbox (reusable MediaPreview component) ── */}
      <MediaPreview {...previewProps} />

      {/* ── Image crop modal — shown after file selection, before upload ── */}
      {showCropModal && cropSrc && (
        <Modal
          isOpen={showCropModal}
          onClose={handleCropCancel}
          header="Crop Profile Photo"
          size="lg"
        >
          <div className="flex flex-col items-center gap-4 p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
              Drag the handles to select the area you want. The image will be
              cropped to a square.
            </p>

            {/* ReactCrop with 1:1 aspect ratio and circular preview */}
            <div className="w-full overflow-auto flex justify-center">
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={1}
                circularCrop
                keepSelection
              >
                <img
                  ref={imgRef}
                  src={cropSrc}
                  alt="Crop preview"
                  onLoad={onCropImageLoad}
                  style={{ maxHeight: "55vh", maxWidth: "100%", objectFit: "contain" }}
                />
              </ReactCrop>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3 w-full justify-end pt-2 border-t border-gray-100 dark:border-gray-700">
              <button
                type="button"
                onClick={handleCropCancel}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCropConfirm}
                disabled={!completedCrop}
                className="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors"
              >
                Crop &amp; Upload
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

export default ProfileHeader;
